import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSettings } from '../context/SettingsContext';
import { useT } from '../i18n';
import { ProfileMenu } from './ProfileMenu';

interface Props {
  children: ReactNode;
  isAdmin: boolean;
  currentPage: string;
  onNavigate: (page: string) => void;
}

/**
 * Standard design-kit shell: topbar + collapsible sidebar (same pattern as
 * PMO/RCAJO — .sidebar-toggle / .shell--collapsed). The collapsed state is
 * persisted so an intervenant who wants the full width for session entry
 * keeps it across visits.
 */
export function AppShell({ children, isAdmin, currentPage, onNavigate }: Props) {
  const { user, logout, impersonatedBy, stopImpersonating } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { logoUrl } = useSettings();
  const { t } = useT();
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem('evolution.sidebar') === 'collapsed',
  );

  useEffect(() => {
    localStorage.setItem('evolution.sidebar', collapsed ? 'collapsed' : 'open');
  }, [collapsed]);

  const navItem = (page: string, label: string) => (
    <a
      href={`#${page}`}
      className={`nav-link ${currentPage === page ? 'active' : ''}`}
      onClick={(e) => { e.preventDefault(); onNavigate(page); }}
    >
      {label}
    </a>
  );

  return (
    <>
      <header className="topbar">
        <div className="topbar-left">
          <div className="brand">
            {logoUrl && (
              <img
                src={logoUrl}
                alt=""
                className="brand-logo"
                onClick={() => onNavigate('upcoming')}
                style={{ cursor: 'pointer' }}
              />
            )}
            <span className="brand-mark" onClick={() => onNavigate('upcoming')} style={{ cursor: 'pointer' }}>
              {t('brand.title')}
            </span>
            <span className="brand-sep" />
            <span className="brand-page">{t('brand.subtitle')}</span>
          </div>
        </div>
        <div className="topbar-right">
          <button
            className="theme-btn"
            onClick={toggleTheme}
            title={theme === 'light' ? t('common.theme.dark') : t('common.theme.light')}
          >
            {theme === 'light' ? '☾' : '☀'}
          </button>
          <ProfileMenu />
          {isAdmin && (
            <span
              className={`badge badge-accent badge-clickable ${currentPage === 'admin' ? 'active' : ''}`}
              onClick={() => onNavigate('admin')}
              title={t('nav.admin')}
            >
              {t('nav.admin')}
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={logout}>{t('common.logout')}</button>
        </div>
      </header>

      <div className={`shell ${collapsed ? 'shell--collapsed' : ''}`}>
        <aside className="sidebar">
          <button
            className="sidebar-toggle"
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? t('nav.expand') : t('nav.collapse')}
            aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
            aria-expanded={!collapsed}
          >
            {collapsed ? '›' : `‹ ${t('nav.collapse')}`}
          </button>
          <nav className="sidebar-nav">
            <div className="nav-section">
              {navItem('upcoming', t('nav.upcoming'))}
              {navItem('participants', t('nav.myParticipants'))}
              {navItem('cards', t('nav.cards'))}
              {isAdmin && navItem('insights', t('nav.insights'))}
            </div>
          </nav>
        </aside>

        <main className="content">
          {impersonatedBy && (
            <div className="impersonation-banner">
              <span>
                {t('common.impersonating')} <strong>{user?.name}</strong> — {t('common.impersonatingBy')} {impersonatedBy.name}
              </span>
              <button
                className="btn btn-sm"
                onClick={async () => {
                  // Await the stop request before reloading: a synchronous
                  // reload() aborts the in-flight POST /admin/stop-impersonate,
                  // leaving the session still impersonating after the reload.
                  try { await stopImpersonating(); } catch { /* keep banner if it failed */ }
                  window.location.reload();
                }}
              >
                {t('common.stopImpersonating')}
              </button>
            </div>
          )}
          <div className="page">{children}</div>
        </main>
      </div>
    </>
  );
}
